/**
 * Switch Panel Component for Hot & Cold Section in MultiViewLobby
 * Renders the toggle UI for switching between Hot games and Cold games (games based on their current static RTP)
 */

import React, { useCallback, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  useDismissOnOutsideClick,
  useDismissOnOutsideTap,
  useDismissOnScroll,
} from 'shared/hooks/usePointerHandlers';
import ToggleSwitch from 'shared/components/ToggleSwitch/ToggleSwitch';
import { TOGGLE_SWITCH_VARIANT } from 'shared/components/ToggleSwitch/toggleSwitchConstants';
import TransWithFallback from 'shared/components/Translations/TransWrapper';
import styles from './HotColdSwitchPanel.module.css';

const InfoIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="9" height="24" viewBox="0 0 9 24" fill="none">
    <path
      d="M6.6666 19.7688C6.6666 20.9374 6.6666 21.9948 8.07481 22.2178C8.20716 22.2264 8.33648 22.2623 8.45504 22.3234C8.5736 22.3845 8.67897 22.4695 8.76487 22.5734C8.85077 22.6772 8.91544 22.7978 8.95501 22.928C8.99459 23.0581 9.00826 23.1951 8.99521 23.3308L8.99521 23.4975C8.88706 24.0541 8.29167 23.9985 7.91258 23.9985L1.03661 23.9985C0.711596 23.9985 0.224356 23.9985 0.116206 23.5531C-0.046019 22.9966 0.172535 22.4401 0.657521 22.3845C2.71462 22.0504 2.38961 20.4365 2.38961 18.9899L2.38961 12.4225C2.38961 11.1428 2.55184 9.75121 0.711596 9.58501C0.170283 9.5271 -0.0460176 9.02851 0.00805742 8.47201C0.0643855 7.9711 1.03661 7.80432 1.14476 7.74873C7.37014 6.74691 6.61197 5.07741 6.66491 11.9761L6.6666 19.7688Z"
      fill="white"
    />
    <path
      d="M5.52947 0.0126709C6.23301 -0.0985136 7.26156 0.51358 7.26156 2.18308C7.26156 3.90818 6.23301 4.85441 4.55499 4.91C3.14679 4.96791 2.1188 4.68763 2.1188 2.85077C2.11936 0.958317 3.09384 0.123276 5.52947 0.0126709Z"
      fill="white"
    />
  </svg>
);

const CloseIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 22 22" fill="none">
    <path
      d="M0.869949 1.17905C0.210017 1.84122 0.210017 2.92904 0.869949 3.5912L7.94066 10.6858C8.03493 10.7804 8.03493 10.9695 7.94066 11.0641L1.01137 18.0169C0.257156 18.7736 0.257156 20.0034 1.01137 20.8074C1.76557 21.5642 2.99116 21.5642 3.79251 20.8074L10.7218 13.8547C10.8161 13.7601 11.0046 13.7601 11.0989 13.8547L18.0282 20.8074C18.7824 21.5642 20.008 21.5642 20.8093 20.8074C21.5636 20.0507 21.5636 18.8209 20.8093 18.0169L13.8801 11.0641C13.7858 10.9695 13.7858 10.7804 13.8801 10.6858L20.8093 3.73306C21.5636 2.97631 21.5636 1.74662 20.8093 0.942568C20.0551 0.185811 18.8295 0.185811 18.0282 0.942568L11.0989 7.89525C11.0046 7.98984 10.8161 7.98984 10.7218 7.89525L3.83965 0.989853C3.08544 0.233097 1.85985 0.233097 1.0585 0.989853L0.869949 1.17905Z"
      fill="#CD0000"
      stroke="#CD0000"
      strokeWidth="0.75"
    />
  </svg>
);

interface HotColdSwitchPanelProps {
  isColdSelected: boolean;
  onToggle: (isSelected: boolean) => void;
}

export const HotColdSwitchPanel: React.FC<HotColdSwitchPanelProps> = ({ isColdSelected, onToggle }) => {
  const { t } = useTranslation();

  const [showInfo, setShowInfo] = useState(false);
  const [infoEl, setInfoEl] = useState<HTMLElement | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);

  const closeInfo = useCallback(() => setShowInfo(false), []);

  // Parent container that has scroll
  const getGamesScroller = useCallback(
    () => rootRef.current?.closest('[data-section]')?.querySelector('[data-grid-scroll-container]') ?? null,
    [],
  );

  useDismissOnScroll(showInfo, closeInfo, getGamesScroller);
  useDismissOnOutsideClick(showInfo, infoEl, closeInfo);
  useDismissOnOutsideTap(showInfo, infoEl, closeInfo);

  return (
    <div ref={rootRef} className={styles.toggleRow}>
      <ToggleSwitch
        variant={TOGGLE_SWITCH_VARIANT.SEGMENTED}
        checked={isColdSelected}
        onChange={onToggle}
        leftLabel={t('sections.hot_and_cold.hot_switch', 'Hot')}
        rightLabel={t('sections.hot_and_cold.cold_switch', 'Cold')}
      />
      {showInfo ? (
        <div
          ref={setInfoEl}
          className={`${styles.infoContainer} ${isColdSelected ? styles.infoThemeCold : styles.infoThemeHot}`}
        >
          <button className={styles.closeButton} onClick={() => setShowInfo(false)}>
            <CloseIcon />
          </button>
          <div className={styles.infoText}>
            <TransWithFallback
              i18nKey={'sections.hot_and_cold.info_text'}
              components={{
                b: <b />,
              }}
            />
          </div>
        </div>
      ) : (
        <button
          className={`${styles.infoButton} ${isColdSelected ? styles.infoThemeCold : styles.infoThemeHot}`}
          onClick={() => setShowInfo(true)}
        >
          <InfoIcon />
        </button>
      )}
    </div>
  );
};
